import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isEmailValid } from "utils/strings";
import { getCallbackURL } from "utils/redirect";
import {
  MAGIC_LINK_ERROR_PARAM,
  getMagicLinkErrorMessage,
} from "utils/magicLinkError";
import { useCache } from "utils/useCache";
import { useAuthStore } from "stores/AuthStoreProvider";
import { CoreAPIError, coreapi } from "services/coreapi";

const MAGIC_LINK_TIMEOUT_SECONDS = 60;

const FORGOT_PARAM = "forgot";

/** Opens the login page straight on the "Forgot password?" form. */
export const FORGOT_PASSWORD_PATH = `/login?${FORGOT_PARAM}=1`;

/** True when the URL asks for the "Forgot password?" form. */
export function wantsForgotForm(search: string): boolean {
  return new URLSearchParams(search).has(FORGOT_PARAM);
}

export const OFFLINE_MESSAGE =
  "You seem to be offline. Check your connection and try again.";

/** A failed `fetch` rejects with a TypeError. */
export function isOfflineError(err: unknown): boolean {
  return (
    (typeof navigator !== "undefined" && navigator.onLine === false) ||
    err instanceof TypeError
  );
}

/** What to tell the user when an email (login link, reset link) couldn't be sent. */
function describeSendError(err: unknown, what = "login link"): string {
  if (isOfflineError(err)) {
    return OFFLINE_MESSAGE;
  }
  if (err instanceof CoreAPIError && err.status === 429) {
    return "Too many attempts. Please wait a minute and try again.";
  }
  return `We couldn't send the ${what}. Please try again.`;
}

export type UseLoginForm = ReturnType<typeof useLoginForm>;

export function useLoginForm() {
  const authStore = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [isValid, setIsValid] = React.useState<boolean>(true);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [linkError, setLinkError] = React.useState<string | null>(null);
  const [countdown, setCountdown] = React.useState<number>(0);
  const [resetSent, setResetSent] = React.useState<boolean>(false);

  // A failed magic-link verify redirects here with `?error=<code>`. Surface
  // it as a clear "your link is old" banner, then scrub the param from the
  // URL (keeping the rest, e.g. `redirect`) so a refresh doesn't re-show it.
  // Same for `?forgot=1`: it only picks the first form (LoginPage has read it
  // by now), and a refresh must not reopen it.
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const message = getMagicLinkErrorMessage(params);
    if (!message && !params.has(FORGOT_PARAM)) {
      return;
    }
    if (message) {
      setLinkError(message);
      params.delete(MAGIC_LINK_ERROR_PARAM);
    }
    params.delete(FORGOT_PARAM);
    const search = params.toString();
    navigate(location.pathname + (search ? "?" + search : ""), {
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The last link sent, with the address it went to: the wait applies to
  // that address only, so a typo can be corrected right away.
  const magicLinkCache = React.useRef(
    useCache<{ sentAt: number; email: string }>({
      versionID: "2",
      key: "magic-link-timeout",
      defaultState: { sentAt: 0, email: "" },
    }),
  ).current;

  const computeRemaining = React.useCallback(
    (forEmail: string): number => {
      const { sentAt, email: sentTo } = magicLinkCache.get();
      if (!sentAt || sentTo !== forEmail.trim().toLowerCase()) {
        return 0;
      }
      const elapsed = Math.floor((Date.now() - sentAt) / 1000);
      return Math.max(0, MAGIC_LINK_TIMEOUT_SECONDS - elapsed);
    },
    [magicLinkCache],
  );

  // Restore a running countdown across a refresh: prefill the address it
  // was sent to, so the "check your inbox" state comes back with it.
  React.useEffect(() => {
    const { sentAt, email: sentTo } = magicLinkCache.get();
    if (sentAt && sentTo && computeRemaining(sentTo) > 0) {
      setEmail(sentTo);
      setCountdown(computeRemaining(sentTo));
    }
  }, [computeRemaining, magicLinkCache]);

  // Tick every second while the countdown is active.
  React.useEffect(() => {
    if (countdown <= 0) {
      return;
    }
    const intervalID = setInterval(() => {
      setCountdown(computeRemaining(email));
    }, 1000);
    return () => clearInterval(intervalID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown > 0, computeRemaining, email]);

  function handleEmailChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const next = e.target.value;
    setEmail(next);
    setIsValid(true);
    setError(null);
    setResetSent(false);
    // Another address: no wait for it (the sent one keeps its own).
    setCountdown(computeRemaining(next));
  }

  // Counts requests and form switches: a reply that is no longer the latest
  // belongs to a form the user left, and must not show up on the next one.
  const requestSeq = React.useRef(0);

  function startRequest(): () => boolean {
    const id = ++requestSeq.current;
    setIsLoading(true);
    return () => id === requestSeq.current;
  }

  /** Each form shows its own messages: wipe them when the user switches form. */
  function clearMessages(): void {
    requestSeq.current += 1;
    setIsLoading(false);
    setIsValid(true);
    setError(null);
    setResetSent(false);
  }

  function validateEmail(): boolean {
    setIsValid(true);
    setError(null);
    if (!isEmailValid(email)) {
      setIsValid(false);
      return false;
    }
    return true;
  }

  async function loginWithMagicLink() {
    if (!validateEmail()) {
      return;
    }
    const isCurrent = startRequest();
    try {
      const callbackURL = getCallbackURL(new URLSearchParams(location.search));
      await authStore.sendMagicLink({ email, callbackURL });

      // Not gated: the link did go out, whichever form is showing now.
      setLinkError(null);
      magicLinkCache.set({
        sentAt: Date.now(),
        email: email.trim().toLowerCase(),
      });
      setCountdown(MAGIC_LINK_TIMEOUT_SECONDS);
    } catch (err) {
      console.error(err);
      if (isCurrent()) {
        setError(describeSendError(err));
      }
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  }

  async function loginWithPassword() {
    if (!validateEmail()) {
      return;
    }
    const isCurrent = startRequest();
    try {
      // No navigate here: RouteGuard moves the signed-in user off /login to
      // `?redirect=`. A second redirect races it and can leave a blank page.
      await authStore.loginWithPassword({ email, password });
    } catch (err) {
      console.error(err);
      if (isCurrent()) {
        setError("Invalid email or password");
      }
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  }

  async function requestPasswordReset() {
    if (!validateEmail()) {
      return;
    }
    const isCurrent = startRequest();
    try {
      await coreapi.requestPasswordReset({ email: email.trim() });
      if (isCurrent()) {
        setResetSent(true);
      }
    } catch (err) {
      console.error(err);
      if (isCurrent()) {
        setError(describeSendError(err, "reset link"));
      }
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  }

  return {
    email,
    password,
    isValid,
    isLoading,
    error,
    linkError,
    countdown,
    resetSent,
    setPassword,
    handleEmailChange,
    clearMessages,
    loginWithMagicLink,
    loginWithPassword,
    requestPasswordReset,
  };
}
